import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { TRIP_TYPES, todayISO } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/admin/DateInput";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field, MoneyInput, BigButton } from "@/components/admin/common";

const EMPTY = { customer_name: "", customer_phone: "", customer_email: "", trip_type: "Fun Dive", start_date: todayISO(), end_date: todayISO(),
  boat_id: null, pax: 1, price: 0, vendor_cost: 0, vendor_due_date: "", notes: "", status: "upcoming" };

export default function BookingForm({ open, onClose, booking, onSaved }) {
  const [f, setF] = useState(EMPTY);
  const [boats, setBoats] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(booking ? { ...EMPTY, ...booking } : { ...EMPTY, start_date: todayISO(), end_date: todayISO() });
    api.get("/admin/boats").then((r) => setBoats(r.data));
  }, [open, booking]);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...f, pax: Number(f.pax) || 1, price: Number(f.price) || 0, vendor_cost: Number(f.vendor_cost) || 0 };
      const r = booking ? await api.put(`/admin/bookings/${booking.id}`, body) : await api.post("/admin/bookings", body);
      toast.success(booking ? "Booking updated" : `Booking ${r.data.code} created`);
      onSaved(r.data);
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-admin max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="booking-form-dialog">
        <DialogHeader><DialogTitle>{booking ? `Edit ${booking.code}` : "New booking"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
          <Field label="Customer name *" className="sm:col-span-2"><Input required value={f.customer_name} onChange={set("customer_name")} className="h-12" data-testid="booking-customer-input" /></Field>
          <Field label="Phone / WhatsApp"><Input value={f.customer_phone} onChange={set("customer_phone")} className="h-12" data-testid="booking-phone-input" /></Field>
          <Field label="Email"><Input type="email" value={f.customer_email} onChange={set("customer_email")} className="h-12" data-testid="booking-email-input" /></Field>
          <Field label="Trip type">
            <Select value={f.trip_type} onValueChange={set("trip_type")}>
              <SelectTrigger className="h-12" data-testid="booking-trip-select"><SelectValue /></SelectTrigger>
              <SelectContent>{TRIP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Boat">
            <Select value={f.boat_id || "none"} onValueChange={(v) => set("boat_id")(v === "none" ? null : v)}>
              <SelectTrigger className="h-12" data-testid="booking-boat-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No boat / not decided</SelectItem>
                {boats.map((b) => <SelectItem key={b.id} value={b.id}>{b.name} ({b.capacity} pax)</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Start date"><DateInput required value={f.start_date} onChange={(v) => v && setF((p) => ({ ...p, start_date: v, end_date: p.end_date < v ? v : p.end_date }))} testid="booking-start-input" /></Field>
          <Field label="End date"><DateInput required min={f.start_date} value={f.end_date} onChange={(v) => v && set("end_date")(v)} testid="booking-end-input" /></Field>
          <Field label="Pax (guests)"><Input type="number" min="1" value={f.pax} onChange={set("pax")} className="h-12" data-testid="booking-pax-input" /></Field>
          <Field label="Total price to guest"><MoneyInput value={f.price} onChange={set("price")} testid="booking-price-input" /></Field>
          <Field label="Cost to boat vendor"><MoneyInput value={f.vendor_cost} onChange={set("vendor_cost")} testid="booking-vendor-cost-input" /></Field>
          <Field label="Vendor payment due"><DateInput value={f.vendor_due_date} onChange={set("vendor_due_date")} testid="booking-vendor-due-input" /></Field>
          <Field label="Notes" className="sm:col-span-2"><Textarea value={f.notes} onChange={set("notes")} data-testid="booking-notes-input" /></Field>
          <BigButton type="submit" disabled={busy} className="sm:col-span-2" data-testid="booking-save-btn">{busy ? "Saving…" : "Save booking"}</BigButton>
        </form>
      </DialogContent>
    </Dialog>
  );
}
